/* eslint-disable @typescript-eslint/naming-convention */

import React, { useCallback, useState } from 'react';
import useUI from 'hooks/use-ui';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

import { PersonalizedProductsQuery, RecommendedProductsQuery } from 'src/personalization/data-layer/types';

import EmptyLoadingState from 'src/components/empty-loading-state';
import { Content } from 'src/components/hybrid-carousel/hybrid-carousel.styles';

import { usePersonalizedProductsCarousel } from '../../use-personalized-products-carousel';
import { useBuyItAgainProducts } from '../../use-buy-it-again-products';

import { BuyItAgainViewAll } from './buy-it-again-view-all';
import { TabSlider } from './tab-slider';
import { Header } from './header';

import { StyledTabContext, StyledTabs, StyledTab, StyledTabPanel } from './tabbed-carousel.styles';

type TabbedPersonalizedProductCarouselProps = {
  heading?: string;
  icon?: React.ReactNode;
  personalizedProductsQuery: PersonalizedProductsQuery;
  recommendedProductsQuery: RecommendedProductsQuery;
  subheading?: string;
};

const TABS = {
  TOP_PICKS: 'top-picks',
  BUY_IT_AGAIN: 'buy-it-again',
};
type Tabs = typeof TABS[keyof typeof TABS];

const TabProps = (key: Tabs, selectedTab: Tabs): Record<string, boolean | string> => {
  const id = `${key}-tab`;
  return {
    'aria-controls': `${key}-tab-panel`,
    'aria-selected': selectedTab === key,
    'data-testid': id,
    id,
    value: key,
  };
};

const TabPanelProps = (key: Tabs): Record<string, string> => {
  const id = `${key}-tab-panel`;
  return {
    'aria-labelledby': `${key}-tab`,
    'data-testid': id,
    id,
    value: key,
  };
};

export const TabbedPersonalizedProductCarousel = ({
  heading,
  icon,
  personalizedProductsQuery,
  recommendedProductsQuery,
  subheading,
}: TabbedPersonalizedProductCarouselProps): JSX.Element | null => {
  const [selectedTab, setSelectedTab] = useState(TABS.TOP_PICKS);
  const { isKiosk } = useUI();
  const isKioskProEnabled = useKioskPro();
  const hideViewAllOrders = isKiosk && isKioskProEnabled;

  const {
    handleProductClick,
    handleQuickAddToCart,
    products,
    error,
    loading,
    viewportVisibility: { ref: topPicksRef },
  } = usePersonalizedProductsCarousel({ personalizedProductsQuery });

  const {
    products: buyItAgainProducts,
    loading: buyItAgainLoading,
    error: buyItAgainError,
    handleProductClick: handleBuyItAgainProductClick,
    handleQuickAddToCart: handleBuyItAgainQuickAddToCart,
    handleViewAllClick,
    viewportVisibility: { ref: buyItAgainRef },
  } = useBuyItAgainProducts({
    recommendedProductsQuery,
  });

  const handleTabChange = useCallback(
    (_, newValue) => {
      if (selectedTab !== newValue) {
        setSelectedTab(newValue);
      }
    },
    [selectedTab]
  );

  if (buyItAgainError) {
    console.error("Error fetching user's previously purchased products.", buyItAgainError);
  }

  const buyItAgainProductCount = buyItAgainProducts.length;
  const showBuyItAgainProducts = !buyItAgainLoading && buyItAgainProductCount > 0;
  const isLoading = loading || buyItAgainLoading;

  if (error || !products.length) {
    return null;
  }

  return (
    <div data-testid='personalized-products-carousel'>
      <EmptyLoadingState page='personalizedCarousel' isLoading={isLoading} tabbedHeader>
        <Content>
          <Header heading={heading} icon={icon} subheading={subheading} />

          {showBuyItAgainProducts ? (
            <StyledTabContext value={selectedTab}>
              <StyledTabs
                defaultValue={TABS.TOP_PICKS}
                value={selectedTab}
                onChange={handleTabChange}
                selectionFollowsFocus
              >
                <StyledTab label='Top picks' {...TabProps(TABS.TOP_PICKS, selectedTab)} />
                <StyledTab label='Buy it again' {...TabProps(TABS.BUY_IT_AGAIN, selectedTab)} />
              </StyledTabs>
              <StyledTabPanel {...TabPanelProps(TABS.TOP_PICKS)} ref={topPicksRef}>
                {selectedTab === TABS.TOP_PICKS && (
                  <TabSlider
                    products={products}
                    onProductClick={handleProductClick}
                    onQuickAddToCart={handleQuickAddToCart}
                    isLoading={Boolean(isLoading)}
                  />
                )}
              </StyledTabPanel>
              <StyledTabPanel {...TabPanelProps(TABS.BUY_IT_AGAIN)} ref={buyItAgainRef}>
                {selectedTab === TABS.BUY_IT_AGAIN && (
                  <TabSlider
                    products={buyItAgainProducts}
                    onProductClick={handleBuyItAgainProductClick}
                    onQuickAddToCart={handleBuyItAgainQuickAddToCart}
                    viewAllCard={!hideViewAllOrders && <BuyItAgainViewAll handleViewAllClick={handleViewAllClick} />}
                    isLoading={Boolean(isLoading)}
                  />
                )}
              </StyledTabPanel>
            </StyledTabContext>
          ) : (
            <TabSlider
              products={products}
              onProductClick={handleProductClick}
              onQuickAddToCart={handleQuickAddToCart}
              isLoading={Boolean(isLoading)}
            />
          )}
        </Content>
      </EmptyLoadingState>
    </div>
  );
};
